import type { Restaurant } from '@shared/types/restaurant';
import type { TimeSlotMenu } from '../types/timeSlots';

export interface ResolvedTip {
  tips: boolean;
  options: number[];
  legacyFallback: boolean;
}

// Mirrors the backend's TipRates::parsePercent/normalizeStored (never throws
// on untrusted config: a non-array reads as empty, and each entry is parsed
// independently — numeric strings accepted, non-numeric/out-of-range/non-
// integer values silently dropped rather than rejecting the whole list).
const toRate = (value: unknown): number =>
  typeof value === 'number' ? value
    : typeof value === 'string' && value.trim() !== '' ? Number(value)
      : NaN;

const sortedRates = (rates: unknown): number[] => {
  if (!Array.isArray(rates)) return [];
  return [...new Set(rates.map(toRate).filter((r) => Number.isInteger(r) && r >= 0 && r <= 100))].sort((a, b) => a - b);
};

/**
 * Resolves whether a booking tips and which rates it can choose from.
 * The experience's own `tipMode` decides: 'off' never tips, 'on' tips with
 * its own list (falling back to the venue's list when it sets none), and
 * 'follow' (or no menu at all) defers to the venue switch + list. A legacy
 * menu that only sets `chargeTip` is treated as 'on' with no rates of its
 * own — `legacyFallback` flags that case so callers keep the old
 * un-derived default instead of a resolved one.
 */
export function resolveTip(
  restaurant: Pick<Restaurant, 'allowGratuity' | 'specificServiceChargeOptions'>,
  menu: Pick<TimeSlotMenu, 'chargeTip' | 'tipMode' | 'tipOptions'> | undefined,
): ResolvedTip {
  const venue = sortedRates(restaurant.specificServiceChargeOptions);
  const venueOn = restaurant.allowGratuity === true && venue.length > 0;
  const mode = menu?.tipMode ?? (menu?.chargeTip ? 'on' : undefined);
  if (mode === 'off') return { tips: false, options: [], legacyFallback: false };
  if (mode === 'on') {
    const own = sortedRates(menu?.tipOptions);
    const options = own.length > 0 ? own : venue;
    return { tips: true, options, legacyFallback: options.length === 0 };
  }
  return venueOn ? { tips: true, options: venue, legacyFallback: false } : { tips: false, options: [], legacyFallback: false };
}

/** Median of the configured rates; null when none is configured. */
export function defaultTipPercent(options: readonly number[] | null | undefined): number | null {
  if (!options || options.length === 0) return null;
  const sorted = [...options].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

/**
 * The rate a booking is actually charged: 0 when the resolution charges no
 * tip, the guest's pick when it is one of the offered rates, the default
 * (upper median) otherwise, and undefined when no rate is configured at all
 * — leaving `composeFee`'s own DEFAULT_GRATUITY_PERCENT fallback in charge.
 */
export function effectiveTipPercent(resolved: ResolvedTip, pick: number | null | undefined): number | undefined {
  if (!resolved.tips) return 0;
  if (resolved.legacyFallback) return undefined;
  return pick != null && resolved.options.includes(pick) ? pick : defaultTipPercent(resolved.options) ?? undefined;
}
